import { Fractal } from 'lib/types';
import { ModuleType } from 'lib/types/course';
import { IBlock, NudgeType, SlideType } from 'lib/types/nudge';
import { ComputedSlideType } from './slides/computed-slides';

export enum ContentVariable {
  COMPANY_NAME = 'companyName',
  LEARNER_NAME = 'name',
  LEARNER_STATE = 'state',
  ASSESSMENT_NUM_CORRECT = 'assessmentNumCorrect',
  ASSESSMENT_NUM_MODULES_SKIPPED = 'assessmentNumModulesSkipped',
  ASSESSMENT_NUM_MODULES_REMAINING = 'assessmentNumModulesRemaining',
  ASSESSMENT_NUM_QUESTIONS = 'assessmentNumQuestions',
  ASSESSMENT_PASSED = 'assessmentPassed',
  ASSESSMENT_SCORE = 'assessmentScore',
  ASSESSMENT_TIME_SAVED = 'assessmentTimeSaved',
}

export enum TransitionDirection {
  FORWARD = 'FORWARD',
  BACKWARD = 'BACKWARD',
}

export interface IAssignmentCursor {
  moduleId: number;
  nudgeId: number;
  slideId: number;
}

export interface ILearningCenterAssignmentPage {
  companyId?: number;
  isEnterpriseAccount: boolean;
  customFeedbackEmail?: string;
  customCourseContactEmail?: string;
  assignment: ILearningCenterAssignment;
  availableLanguages: string[];
}

export interface ILearningCenterAssignment {
  id: number;
  name: string;
  assignmentDate: string;
  courseName: string;
  courseId: number;
  imageUrl?: string;
  deadlineDate?: string;
  isBlockedOnTime: boolean;
  isCustomCourseAssignment: boolean;
  lengthInMinutes: number;
  orderedModules: ILearningCenterModule[];
}

export interface ILearningCenterModule {
  moduleId: number;
  moduleName: string;
  moduleType: ModuleType;
  userModuleId?: number;
  isLmseModule: boolean;
  iframeUrl?: string;
  isComplete: boolean;
  orderedNudges: ILearningCenterNudge[];
  hasAssessmentCustomization: boolean;
  assessmentResult?: ILearningCenterAssessmentResult;
}

export interface ILearningCenterNudge {
  nudgeId: number;
  nudgeGroupId: number;
  userNudgeId?: number;
  moduleId: number;
  userModuleId?: number;
  name: string;
  nudgeType: NudgeType;
  isComplete: boolean;
  lengthInMinutes: number;
  contentVariables: Record<string, string>;
  orderedSlides: ILearningCenterSlide[];
}

export interface ILearningCenterSlide {
  slideId: number;
  userSlideId?: number;
  nudgeId: number;
  userNudgeId?: number;
  moduleId: number;
  slideType: SlideType | ComputedSlideType;
  requiredSeconds: number;
  title?: string;
  subtitle?: string;
  audioUrl?: string;
  standaloneAudioUrl?: string;
  blocks: Fractal<IBlock>;
  isComplete: boolean;
  secondsSpent: number;
  question?: ILearningCenterQuestion;
  video?: ILearningCenterVideoBundle;
  rusticiMetadata?: ILearningCenterRusticiMetadata;
}

export interface ILearningCenterVideoBundle extends ISelfHostedVideo {
  companyId?: number;
  language: string;
  audioDescriptionVideo?: ISelfHostedVideo;
  captions?: ISelfHostedCaption[];
}

export interface ISelfHostedCaption {
  id: number;
  language: string;
  url: string;
}

export interface ISelfHostedVideo {
  id: number;
  durationInSeconds: number;
  videoUrl: string;
  hlsManifestUrl?: string;
  mp4Urls: Array<{
    quality: number;
    url: string;
  }>;
}

export interface ILearningCenterQuestion {
  id: number;
  type: string; // TODO: [Learning Center] switch IQuestion over to a proper type
  content: string;
  orderedAnswers: ILearningCenterQuestionAnswer[];
  selectedAnswerId?: number;
}

export interface ILearningCenterQuestionAnswer {
  id: number;
  content: string;
  isCorrectAnswer: boolean;
  responseText: string;
  responseImageUrl?: string;
}

export interface ILearningCenterSettings {
  areTranscriptsVisible?: boolean;
  isAutoAdvanceEnabled?: boolean;
}

interface ILearningCenterRusticiMetadata {
  fileName: string;
  fileType: string;
  previewIframeUrl?: string;
}

export interface ILearningCenterAssessmentResult {
  totalQuestions: number;
  numCorrectAnswers: number;
  passedAssessment: boolean;
  skippedUserModuleIds: number[];
  skippedModuleNames: string[];
  remainingModuleNames: string[];
  timeSavedInMinutes: number;
}
